import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer'
import type { Resume, ResumeSectionKey } from '../../../types'
import {
  contactInline,
  joinDateRange,
  getSectionOrder,
  labelFor,
  displayUrl,
  BULLET,
  pdfMetadata,
  pdfFontFamily,
  previewFontFamily,
  getAccent,
  type PdfFontFamily,
} from './shared'

/**
 * Executive template — bold dark header band with accent color, strong serif
 * section titles with bottom rule. Aimed at senior / leadership candidates.
 */

const makeStyles = (font: PdfFontFamily, accent: string) =>
  StyleSheet.create({
    page: { paddingTop: 0, paddingBottom: 50, paddingHorizontal: 0, fontSize: 10.5, color: '#111827', fontFamily: font.regular },
    headerBand: { backgroundColor: '#0f172a', color: '#ffffff', paddingTop: 28, paddingBottom: 24, paddingHorizontal: 44, borderBottomWidth: 4, borderBottomColor: accent },
    headerName: { fontSize: 26, fontFamily: font.bold, color: '#ffffff', letterSpacing: 1 },
    headerHeadline: { fontSize: 11, marginTop: 4, color: '#e2e8f0', fontFamily: font.italic },
    headerContacts: { fontSize: 9.5, marginTop: 10, color: '#cbd5e1' },
    body: { paddingTop: 22, paddingHorizontal: 44 },
    sectionTitle: { fontSize: 12, fontFamily: font.bold, textTransform: 'uppercase', letterSpacing: 1.4, color: accent, marginBottom: 6, borderBottomWidth: 1, borderBottomColor: accent, paddingBottom: 2 },
    section: { marginBottom: 12 },
    itemHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 1 },
    itemTitle: { fontSize: 11.5, fontFamily: font.bold, color: '#0f172a' },
    itemMeta: { fontSize: 9.5, color: '#475569', fontFamily: font.italic },
    bulletRow: { flexDirection: 'row', marginTop: 2 },
    bulletDot: { width: 10, fontSize: 10.5, color: accent },
    bulletText: { flex: 1, fontSize: 10.5, lineHeight: 1.45 },
    paragraph: { fontSize: 10.5, lineHeight: 1.5 },
    pageNumber: { position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center', fontSize: 8.5, color: '#94a3b8' },
    linkInline: { textDecoration: 'none', color: accent },
  })

type ExecutiveStyles = ReturnType<typeof makeStyles>

function renderSection(key: ResumeSectionKey, resume: Resume, styles: ExecutiveStyles, font: PdfFontFamily) {
  switch (key) {
    case 'summary':
      if (!resume.summary) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'summary')}</Text>
          <Text style={styles.paragraph}>{resume.summary}</Text>
        </View>
      )
    case 'experience':
      if (resume.experience.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'experience')}</Text>
          {resume.experience.map((exp) => (
            <View key={exp.id} style={{ marginBottom: 7 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{exp.role || exp.company}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(exp.startDate, exp.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[exp.company, exp.location].filter(Boolean).join(' · ')}</Text>
              {exp.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'education':
      if (resume.education.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'education')}</Text>
          {resume.education.map((e) => (
            <View key={e.id} style={{ marginBottom: 4 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{e.institution}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(e.startDate, e.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[e.degree, e.field, e.location].filter(Boolean).join(' · ')}</Text>
              {e.details ? <Text style={styles.paragraph}>{e.details}</Text> : null}
            </View>
          ))}
        </View>
      )
    case 'skills':
      if (resume.skills.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'skills')}</Text>
          {resume.skills.map((g) => (
            <Text key={g.id} style={styles.paragraph}>
              <Text style={{ fontFamily: font.bold }}>{g.category}: </Text>
              {g.items.join(', ')}
            </Text>
          ))}
        </View>
      )
    case 'projects':
      if (resume.projects.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'projects')}</Text>
          {resume.projects.map((p) => (
            <View key={p.id} style={{ marginBottom: 4 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{p.name}</Text>
                {p.url ? <Link src={p.url} style={[styles.itemMeta, styles.linkInline]}>{displayUrl(p.url)}</Link> : null}
              </View>
              {p.description ? <Text style={styles.paragraph}>{p.description}</Text> : null}
              {p.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'certifications':
      if (resume.certifications.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'certifications')}</Text>
          {resume.certifications.map((c) => (
            <Text key={c.id} style={styles.paragraph}>{c.name}{c.issuer ? ` · ${c.issuer}` : ''}{c.date ? ` · ${c.date}` : ''}</Text>
          ))}
        </View>
      )
    case 'languages':
      if (resume.languages.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'languages')}</Text>
          <Text style={styles.paragraph}>{resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}</Text>
        </View>
      )
    case 'customSections':
      return resume.customSections.map((s) => (
        <View key={s.id} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{s.title}</Text>
          <Text style={styles.paragraph}>{s.body}</Text>
        </View>
      ))
  }
}

export function ExecutivePdf({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const meta = pdfMetadata(resume)
  const order = getSectionOrder(resume)
  const font = pdfFontFamily(resume, 'serif')
  const accent = getAccent(resume)
  const styles = makeStyles(font, accent)
  return (
    <Document title={meta.title} author={meta.author} subject={meta.subject}>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerBand} fixed={false}>
          <Text style={styles.headerName}>{resume.contact.fullName || ' '}</Text>
          {resume.contact.headline ? <Text style={styles.headerHeadline}>{resume.contact.headline}</Text> : null}
          {contacts.length > 0 && <Text style={styles.headerContacts}>{contacts.join('  ·  ')}</Text>}
        </View>
        <View style={styles.body}>
          {order.map((key) => renderSection(key, resume, styles, font))}
        </View>
        <Text
          style={styles.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) => (totalPages > 1 ? `${pageNumber} / ${totalPages}` : '')}
        />
      </Page>
    </Document>
  )
}

export function ExecutivePreview({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const order = getSectionOrder(resume)
  const accent = getAccent(resume)
  return (
    <div className="bg-white text-slate-900 shadow-sm rounded-md overflow-hidden text-[12px] leading-snug" style={{ fontFamily: previewFontFamily(resume, 'serif') }}>
      <div className="px-10 pt-7 pb-6 text-white" style={{ backgroundColor: '#0f172a', borderBottom: `4px solid ${accent}` }}>
        <div className="text-3xl font-bold tracking-wide">{resume.contact.fullName || '\u00a0'}</div>
        {resume.contact.headline && <div className="italic text-slate-200 mt-1">{resume.contact.headline}</div>}
        {contacts.length > 0 && <div className="text-slate-300 text-[11px] mt-2">{contacts.join('  ·  ')}</div>}
      </div>
      <div className="px-10 pt-5 pb-8">
        {order.map((key) => {
          const Section = ({ children, title }: { children: React.ReactNode; title: string }) => (
            <div className="mb-3">
              <div className="text-[12px] font-bold uppercase tracking-wider pb-0.5 mb-1.5" style={{ color: accent, borderBottom: `1px solid ${accent}` }}>{title}</div>
              <div>{children}</div>
            </div>
          )
          switch (key) {
            case 'summary':
              if (!resume.summary) return null
              return <Section key={key} title={labelFor(resume, 'summary')}><p>{resume.summary}</p></Section>
            case 'experience':
              if (resume.experience.length === 0) return null
              return (
                <Section key={key} title={labelFor(resume, 'experience')}>
                  {resume.experience.map((e) => (
                    <div key={e.id} className="mb-2">
                      <div className="flex justify-between"><div className="font-bold">{e.role || e.company}</div><div className="italic text-slate-600 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                      <div className="italic text-slate-600 text-[11px]">{[e.company, e.location].filter(Boolean).join(' · ')}</div>
                      <ul className="list-disc pl-5 mt-0.5">{e.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                    </div>
                  ))}
                </Section>
              )
            case 'education':
              if (resume.education.length === 0) return null
              return (
                <Section key={key} title={labelFor(resume, 'education')}>
                  {resume.education.map((e) => (
                    <div key={e.id} className="mb-1.5">
                      <div className="flex justify-between"><div className="font-bold">{e.institution}</div><div className="italic text-slate-600 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                      <div className="italic text-slate-600 text-[11px]">{[e.degree, e.field, e.location].filter(Boolean).join(' · ')}</div>
                      {e.details && <p>{e.details}</p>}
                    </div>
                  ))}
                </Section>
              )
            case 'skills':
              if (resume.skills.length === 0) return null
              return <Section key={key} title={labelFor(resume, 'skills')}>{resume.skills.map((g) => <p key={g.id}><b>{g.category}:</b> {g.items.join(', ')}</p>)}</Section>
            case 'projects':
              if (resume.projects.length === 0) return null
              return (
                <Section key={key} title={labelFor(resume, 'projects')}>
                  {resume.projects.map((p) => (
                    <div key={p.id} className="mb-1.5">
                      <div className="flex justify-between">
                        <div className="font-bold">{p.name}</div>
                        {p.url && <a className="text-[11px]" style={{ color: accent }} href={p.url}>{displayUrl(p.url)}</a>}
                      </div>
                      {p.description && <p>{p.description}</p>}
                      <ul className="list-disc pl-5">{p.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                    </div>
                  ))}
                </Section>
              )
            case 'certifications':
              if (resume.certifications.length === 0) return null
              return <Section key={key} title={labelFor(resume, 'certifications')}>{resume.certifications.map((c) => <p key={c.id}>{c.name}{c.issuer ? ` · ${c.issuer}` : ''}{c.date ? ` · ${c.date}` : ''}</p>)}</Section>
            case 'languages':
              if (resume.languages.length === 0) return null
              return <Section key={key} title={labelFor(resume, 'languages')}><p>{resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}</p></Section>
            case 'customSections':
              return resume.customSections.map((s) => <Section key={s.id} title={s.title}><p className="whitespace-pre-line">{s.body}</p></Section>)
          }
        })}
      </div>
    </div>
  )
}
